
import { Component, Vue, Watch } from 'vue-property-decorator';
import {
  Client,
  IJobModel,
  IJobItemModel,
  ServiceTypeModel,
  PostJobItemBulkUpdateRequest
} from '@/services/api.service';
import {
  EnumModel,
  StatusEnums,
  JobItemTypeEnums
} from '@/common/enumerations';
import LoadingSkeleton from '@/components/commonui/LoadingSkeleton.vue';
import toISODateFormatter from '@/filters/toISOdate.formatter';
import toDateFormatter from '@/filters/todate.formatter';
import toStatusFormatter from '@/filters/status.formatter';
import toJobItemTypeFormatter from '@/filters/jobitemtype.formatter';
import * as StringUtils from '@/utilities/string.utils';

@Component({
  components: {
    LoadingSkeleton
  },
  filters: {
    toISODate: toISODateFormatter,
    toDate: toDateFormatter,
    toStatus: toStatusFormatter,
    toJobItemType: toJobItemTypeFormatter
  }
})
export default class BulkEditJobItems extends Vue {
  private jobGid: string = '';
  private job: IJobModel | null = null;
  private jobItems: IJobItemModel[] = [];
  private selectedJobItemGids: string[] = [];
  private jobNumber: string = '';

  private jobLoaded: boolean = false;
  private jobItemsLoaded: boolean = false;
  private jobIsQuoted: boolean = false;
  private hideCancelledItems: boolean = true;

  // Available options for dropdowns
  private availableJobItemTypes: EnumModel[] = [];
  private availableStatusTypes: EnumModel[] = [];
  private availableServiceTypes: ServiceTypeModel[] = [];
  private filteredServiceTypes: ServiceTypeModel[] = [];
  private enableServiceTypeSelection: boolean = false;

  // Property edit states (which properties are being edited)
  private propertyEditStates: { [key: string]: boolean } = {
    jobItemType: false,
    serviceTypeGid: false,
    name: false,
    quoteValue: false,
    isAdditionalToQuotedProjectScope: false,
    status: false,
    expectedStartDate: false,
    isFullDay: false,
    purchaseOrderNumber: false,
    contactName: false,
    contactPhoneNumber: false,
    notes: false
  };

  // Property values
  private propertyValues: { [key: string]: any } = {
    jobItemType: 0,
    serviceTypeGid: '',
    name: '',
    quoteValue: 0,
    isAdditionalToQuotedProjectScope: false,
    status: 0,
    expectedStartDate: undefined as Date | undefined,
    isFullDay: false,
    purchaseOrderNumber: '',
    contactName: '',
    contactPhoneNumber: '',
    notes: ''
  };

  private get hasSelectedItems(): boolean {
    return this.selectedJobItemGids.length > 0;
  }

  private get hasPropertiesToEdit(): boolean {
    return Object.values(this.propertyEditStates).some(state => state === true);
  }

  private get cancelledItemsCount(): number {
    return this.jobItems.filter(ji => ji.status !== undefined && ji.status === 5).length; // Status.Cancelled
  }

  private get filteredJobItems(): IJobItemModel[] {
    if (this.hideCancelledItems) {
      return this.jobItems.filter(ji => ji.status === undefined || ji.status !== 5); // Status.Cancelled
    }
    return this.jobItems;
  }

  private get selectedJobItems(): IJobItemModel[] {
    return this.filteredJobItems.filter(ji => this.selectedJobItemGids.includes(ji.gid || ''));
  }

  private get expectedStartDateFormatted(): string {
    if (!this.propertyValues.expectedStartDate) {
      return '';
    }
    const date = this.propertyValues.expectedStartDate as Date;
    return toISODateFormatter(date);
  }

  protected mounted() {
    this.jobGid = this.$route.params.gid;
    this.initializeEnums();
    this.fetchJobData();
  }

  private initializeEnums() {
    // Initialize Job Item Types
    const pickJobItemTypeModel: EnumModel = new EnumModel('(Select Job Item Type...)', -1);
    this.availableJobItemTypes.push(
      ...JobItemTypeEnums.sort((n1, n2) => StringUtils.stringCompare(n1.name || '', n2.name || ''))
    );
    this.availableJobItemTypes.unshift(pickJobItemTypeModel);

    // Initialize Status Types
    const pickStatusModel: EnumModel = new EnumModel('(Select Status...)', -1);
    this.availableStatusTypes.push(...StatusEnums);
    this.availableStatusTypes.unshift(pickStatusModel);
  }

  private async fetchJobData() {
    const clientService = new Client(process.env.VUE_APP_API_URL, this.$http);
    
    try {
      // @ts-ignore
      this.$Progress.start();
      
      const [theJob, theJobItems, serviceTypes] = await Promise.all([
        clientService.getJob(this.jobGid),
        clientService.getJobItems(this.jobGid),
        clientService.getServiceTypes()
      ]);

      this.job = theJob;
      this.jobNumber = theJob.jobNumber || '';
      this.jobIsQuoted = theJob.isQuotedProject || false;
      this.jobItems = theJobItems;
      this.availableServiceTypes = serviceTypes.sort((n1, n2) =>
        StringUtils.stringCompare(n1.name || '', n2.name || '')
      );

      this.jobLoaded = true;
      this.jobItemsLoaded = true;

      // @ts-ignore
      this.$Progress.finish();
    } catch (error) {
      // @ts-ignore
      this.$Progress.finish();
      // @ts-ignore
      this.$awn.alert('An error occurred loading job data: ' + error);
    }
  }

  private arePropertyValuesSame(propertyName: string): boolean {
    if (this.selectedJobItems.length === 0) {
      return false;
    }

    const firstValue = this.getPropertyValue(this.selectedJobItems[0], propertyName);
    
    return this.selectedJobItems.every(item => {
      const value = this.getPropertyValue(item, propertyName);
      return this.compareValues(firstValue, value);
    });
  }

  private getPropertyValue(jobItem: IJobItemModel, propertyName: string): any {
    switch (propertyName) {
      case 'jobItemType':
        return jobItem.jobItemType;
      case 'serviceTypeGid':
        return jobItem.serviceTypeGid || '';
      case 'name':
        return jobItem.name || '';
      case 'quoteValue':
        return jobItem.quoteValue || 0;
      case 'isAdditionalToQuotedProjectScope':
        return jobItem.isAdditionalToQuotedProjectScope || false;
      case 'status':
        return jobItem.status;
      case 'expectedStartDate':
        return jobItem.expectedStartDate;
      case 'isFullDay':
        return jobItem.isFullDay || false;
      case 'purchaseOrderNumber':
        return jobItem.purchaseOrderNumber || '';
      case 'contactName':
        return jobItem.contactName || '';
      case 'contactPhoneNumber':
        return jobItem.contactPhoneNumber || '';
      case 'notes':
        return jobItem.notes || '';
      default:
        return null;
    }
  }

  private compareValues(val1: any, val2: any): boolean {
    // Handle date comparison
    if (val1 instanceof Date && val2 instanceof Date) {
      return val1.getTime() === val2.getTime();
    }
    if (val1 instanceof Date || val2 instanceof Date) {
      return false;
    }
    
    // Handle undefined/null
    if ((val1 === null || val1 === undefined) && (val2 === null || val2 === undefined)) {
      return true;
    }
    if ((val1 === null || val1 === undefined) || (val2 === null || val2 === undefined)) {
      return false;
    }

    return val1 === val2;
  }

  private getCommonPropertyValue(propertyName: string): any {
    if (this.selectedJobItems.length === 0) {
      return this.getDefaultValue(propertyName);
    }

    if (!this.arePropertyValuesSame(propertyName)) {
      return this.getDefaultValue(propertyName);
    }

    return this.getPropertyValue(this.selectedJobItems[0], propertyName);
  }

  private getDefaultValue(propertyName: string): any {
    switch (propertyName) {
      case 'jobItemType':
        return -1;
      case 'serviceTypeGid':
        return '';
      case 'name':
        return '';
      case 'quoteValue':
        return 0;
      case 'isAdditionalToQuotedProjectScope':
        return false;
      case 'status':
        return -1;
      case 'expectedStartDate':
        return undefined;
      case 'isFullDay':
        return false;
      case 'purchaseOrderNumber':
        return '';
      case 'contactName':
        return '';
      case 'contactPhoneNumber':
        return '';
      case 'notes':
        return '';
      default:
        return null;
    }
  }

  private updatePropertyValues() {
    // Update all property values based on selected items
    const properties = Object.keys(this.propertyEditStates);
    properties.forEach(prop => {
      if (this.hasSelectedItems) {
        this.propertyValues[prop] = this.getCommonPropertyValue(prop);
      } else {
        this.propertyValues[prop] = this.getDefaultValue(prop);
      }
    });

    // Update service type filtering
    if (this.propertyValues.jobItemType !== undefined && this.propertyValues.jobItemType !== -1) {
      this.filterServiceTypes(this.propertyValues.jobItemType);
    }
  }

  private filterServiceTypes(selectedJobItemType: number) {
    if (selectedJobItemType === -1) {
      this.filteredServiceTypes = [];
      const pickServiceTypeModel: ServiceTypeModel = new ServiceTypeModel();
      pickServiceTypeModel.init({ name: '(Select Job Item Type First...)', gid: '' });
      this.filteredServiceTypes.push(pickServiceTypeModel);
      this.propertyValues.serviceTypeGid = '';
      this.enableServiceTypeSelection = false;
    } else {
      let theFilteredList: ServiceTypeModel[] = [];
      theFilteredList = this.availableServiceTypes.filter(
        st => st.parentJobType === 0 || st.parentJobType === selectedJobItemType
      );
      if (theFilteredList.length !== 1) {
        const pickServiceTypeModel: ServiceTypeModel = new ServiceTypeModel();
        pickServiceTypeModel.init({ name: '(Select Service Type...)', gid: '' });
        theFilteredList.unshift(pickServiceTypeModel);
      }
      this.filteredServiceTypes = theFilteredList;
      this.enableServiceTypeSelection = true;

      // Check if current serviceTypeGid is still valid
      const currentServiceTypeGid = this.propertyValues.serviceTypeGid;
      const isValid = this.filteredServiceTypes.some(st => st.gid === currentServiceTypeGid);
      if (!isValid && currentServiceTypeGid) {
        this.propertyValues.serviceTypeGid = '';
      }
    }
  }

  private onExpectedStartDateInput(event: any) {
    const dateValue = event.target.valueAsDate;
    this.propertyValues.expectedStartDate = dateValue || undefined;
  }

  private convertPropertyValueToString(value: any, propertyName: string): string {
    if (value === null || value === undefined) {
      return '';
    }

    switch (propertyName) {
      case 'expectedStartDate':
        if (value instanceof Date) {
          // Convert to ISO date string (YYYY-MM-DD format)
          return toISODateFormatter(value);
        }
        return '';
      case 'isFullDay':
      case 'isAdditionalToQuotedProjectScope':
        return value ? 'true' : 'false';
      case 'jobItemType':
      case 'status':
      case 'quoteValue':
        return String(value);
      default:
        return String(value);
    }
  }

  private saveBulkEdit() {
    if (!this.hasSelectedItems || !this.hasPropertiesToEdit) {
      // @ts-ignore
      this.$awn.warning('Please select at least one job item and enable at least one property to edit.');
      return;
    }

    const properties: { [key: string]: string } = {};

    // Build properties dictionary with only checked properties
    Object.keys(this.propertyEditStates).forEach(prop => {
      if (this.propertyEditStates[prop]) {
        const value = this.propertyValues[prop];
        // For Gid properties, skip if empty string (should be undefined/null instead)
        if (prop.endsWith('Gid') && (value === '' || value === null || value === undefined)) {
          return; // Skip this property
        }
        const stringValue = this.convertPropertyValueToString(value, prop);
        // Only add non-empty values for Gid properties
        if (prop.endsWith('Gid') && stringValue === '') {
          return; // Skip empty Gid properties
        }
        properties[prop] = stringValue;
      }
    });

    const request = new PostJobItemBulkUpdateRequest({
      jobItemGids: this.selectedJobItemGids,
      properties: properties
    });

    const clientService = new Client(process.env.VUE_APP_API_URL, this.$http);
    
    // @ts-ignore
    this.$Progress.start();

    clientService.updateBulkJobItems(this.jobGid, request)
      .then(() => {
        // @ts-ignore
        this.$Progress.finish();
        // @ts-ignore
        this.$awn.success('Job items updated successfully');
        this.$router.push({ name: 'jobview', params: { gid: this.jobGid } });
      })
      .catch((error: any) => {
        // @ts-ignore
        this.$Progress.finish();
        const errMsg = 'An error occurred when updating job items: <br/><br/>' + error;
        // @ts-ignore
        this.$awn.alert(errMsg);
      });
  }

  private cancelEdit() {
    this.$router.push({ name: 'jobview', params: { gid: this.jobGid } });
  }

  @Watch('selectedJobItemGids')
  private onSelectedJobItemsChanged() {
    this.updatePropertyValues();
  }

  @Watch('propertyValues.jobItemType')
  private onJobItemTypeChanged(value: number) {
    if (value !== undefined) {
      this.filterServiceTypes(value);
    }
  }

  private getStatus(value: number): string {
    return toStatusFormatter(value).toLowerCase();
  }
}
